import React, { useState } from 'react';
import ImageViewer from '../common/ImageViewer';
import { COLORS, FONTS } from '../../styles/tokens';

// Fremdriften i et oppdrag slik bestilleren ser den: hva sjåføren har registrert ved
// henting og levering (bilder, vekt, veieseddel, signatur), før bestilleren bekrefter.
// Alt leses fra ordredokumentet, som oppdateres i sanntid mens sjåføren jobber.

const STAGE = { open: 0, matched: 1, in_progress: 2, delivered_pending: 3, disputed: 3, completed: 4, invoiced: 4 };

const num = (n) => Number(n).toLocaleString('nb-NO');
const fmt = (ts) => ts?.toDate?.()?.toLocaleString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function OrderTimeline({ order }) {
  const [viewer, setViewer] = useState(null); // { url, label, signature }
  const stage = STAGE[order.status] ?? 0;
  const unit  = order.unit === 'bags' ? 'sekker' : 'tonn';
  const adj   = order.pendingAdjustment;

  const steps = [
    {
      key: 'created',
      title: 'Oppdrag opprettet',
      time: fmt(order.createdAt),
      done: true,
    },
    {
      key: 'matched',
      title: 'Sjåfør tildelt',
      done: stage >= 1,
    },
    {
      key: 'pickup',
      title: 'Masse hentet',
      time: fmt(order.pickupRegisteredAt),
      done: stage >= 2 || !!order.pickupRegisteredAt,
      body: (order.pickupRegisteredAt || order.pickupActualTons != null) && (
        <>
          {order.pickupActualTons != null && (
            <p style={styles.text}>
              <strong>{num(order.pickupActualTons)} {unit}</strong> hentet
              {order.estimatedTons != null && (
                <span style={{ color: COLORS.textLight }}> (bestilt {num(order.estimatedTons)} {unit})</span>
              )}
            </p>
          )}
          {adj && (
            <p style={{ ...styles.text, color: adj.approvedByOrderer ? COLORS.textMid : COLORS.warningText }}>
              Vektavvik {adj.deviationPct}% · prisjustering {adj.priceAdjustment > 0 ? '+' : '−'}kr {num(Math.abs(adj.priceAdjustment))}
              {adj.approvedByOrderer ? ' · godkjent' : ' · venter på din godkjenning'}
            </p>
          )}
          <Thumbs
            items={[
              ...(order.pickupPhotoUrls ?? []).map((url, i) => ({ url, label: `Hentebilde ${i + 1}` })),
              ...(order.weightDocumentUrl ? [{ url: order.weightDocumentUrl, label: 'Veieseddel', caption: 'Veieseddel' }] : []),
            ]}
            onOpen={setViewer}
          />
        </>
      ),
    },
    {
      key: 'delivered',
      title: 'Levert',
      time: fmt(order.deliveredAt),
      done: stage >= 3 || !!order.deliveredAt,
      body: order.deliveredAt && (
        <>
          <Thumbs
            items={(order.deliveryPhotoUrls ?? []).map((url, i) => ({ url, label: `Leveringsbilde ${i + 1}` }))}
            onOpen={setViewer}
          />
          {order.receiverSignatureUrl && (
            <div style={{ marginTop: 10 }}>
              <div style={styles.subLabel}>Mottakers signatur</div>
              <button
                style={styles.signatureBtn}
                onClick={() => setViewer({ url: order.receiverSignatureUrl, label: 'Mottakers signatur', signature: true })}
              >
                <img src={order.receiverSignatureUrl} alt="Mottakers signatur" style={styles.signatureImg} />
              </button>
            </div>
          )}
        </>
      ),
    },
    order.status === 'disputed'
      ? { key: 'confirmed', title: 'Levering bestridt', done: true, danger: true,
          body: <p style={styles.text}>Tippi ser på saken og tar kontakt.</p> }
      : {
          key: 'confirmed',
          title: stage >= 4 ? 'Levering bekreftet' : 'Venter på din bekreftelse',
          done: stage >= 4,
          body: order.status === 'delivered_pending' && order.deliveryConfirmDeadline && (
            <p style={styles.text}>
              Se over bildene og signaturen. Bekreftes automatisk {fmt(order.deliveryConfirmDeadline)} hvis du ikke svarer.
            </p>
          ),
        },
  ];

  const currentIdx = steps.findIndex(s => !s.done);
  const receipts = (order.receipts ?? []).filter(r => r?.url);

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Fremdrift</h3>
      <ol style={styles.list}>
        {steps.map((step, i) => {
          const state = step.danger ? 'danger' : step.done ? 'done' : i === currentIdx ? 'current' : 'future';
          const last  = i === steps.length - 1;
          return (
            <li key={step.key} style={styles.step}>
              <div style={styles.rail}>
                <span style={{ ...styles.dot, ...DOT[state] }}>{state === 'done' ? '✓' : state === 'danger' ? '!' : ''}</span>
                {!last && <span style={{ ...styles.line, background: step.done ? COLORS.brandPanel : COLORS.border }} />}
              </div>
              <div style={{ ...styles.content, paddingBottom: last ? 0 : 20 }}>
                <div style={styles.stepHeader}>
                  <span style={{ ...styles.stepTitle, color: state === 'future' ? COLORS.textLight : state === 'danger' ? COLORS.dangerText : COLORS.textDark }}>
                    {step.title}
                  </span>
                  {step.time && <span style={styles.time}>{step.time}</span>}
                </div>
                {step.body}
              </div>
            </li>
          );
        })}
      </ol>
      {receipts.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={styles.subLabel}>Billag</div>
          <Thumbs items={receipts.map((r, i) => ({ url: r.url, label: `Billag ${i + 1}`, caption: `Billag ${i + 1}` }))} onOpen={setViewer} />
          {receipts.map((r, i) => r.note && <p key={r.url} style={styles.text}>Billag {i + 1}: {r.note}</p>)}
        </div>
      )}
      {viewer && <ImageViewer {...viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}

function Thumbs({ items, onOpen }) {
  if (!items.length) return null;
  return (
    <div style={styles.thumbs}>
      {items.map(item => (
        <button key={item.url} style={styles.thumbBtn} onClick={() => onOpen(item)} title={item.label}>
          <img src={item.url} alt={item.label} style={styles.thumb} />
          {item.caption && <span style={styles.thumbCaption}>{item.caption}</span>}
        </button>
      ))}
    </div>
  );
}

const DOT = {
  done:    { background: COLORS.brandPanel, borderColor: COLORS.brandPanel, color: '#fff' },
  current: { background: COLORS.accent, borderColor: COLORS.brandPanel },
  future:  { background: COLORS.white, borderColor: COLORS.border },
  danger:  { background: COLORS.danger, borderColor: COLORS.danger, color: '#fff' },
};

const styles = {
  card:         { background: 'transparent', padding: 0, margin: '18px 0 16px' },
  title:        { fontFamily: FONTS.display, fontSize: 24, fontWeight: 700, color: COLORS.textDark, margin: '0 0 14px' },
  list:         { listStyle: 'none', margin: 0, padding: 0 },
  step:         { display: 'flex', gap: 12 },
  rail:         { display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 22 },
  dot:          { width: 22, height: 22, borderRadius: '50%', border: '2px solid', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 },
  line:         { width: 2, flex: 1, minHeight: 16, marginTop: 2 },
  content:      { flex: 1, minWidth: 0, paddingTop: 1 },
  stepHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  stepTitle:    { fontSize: 15, fontWeight: 700 },
  time:         { fontFamily: 'var(--font-mono)', fontSize: 10.5, color: COLORS.textLight, whiteSpace: 'nowrap' },
  text:         { fontSize: 13, color: COLORS.textMid, margin: '6px 0 0', lineHeight: 1.45 },
  subLabel:     { fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  thumbs:       { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  thumbBtn:     { position: 'relative', padding: 0, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', background: COLORS.surfaceHover, cursor: 'zoom-in', width: 84, height: 70 },
  thumb:        { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  thumbCaption: { position: 'absolute', left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, fontWeight: 600, padding: '2px 4px', textAlign: 'center' },
  signatureBtn: { padding: 8, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: '#ffffff', cursor: 'zoom-in', display: 'block', maxWidth: 260, width: '100%' },
  signatureImg: { width: '100%', height: 'auto', display: 'block' },
};
